/**
 * Subscribe to live presence for a set of user ids.
 *
 *   const userIds = useMemo(() => contacts.map(c => c.id), [contacts])
 *   usePresenceSubscription(userIds)
 *   const me = usePresence(myUserId)
 *
 * Behavior:
 *   - Joins a `user:<id>` Phoenix channel per id.
 *   - Reuses channels across renders; only adds/removes channels when the
 *     set membership changes (set-diffing — string identity stable).
 *   - Wires `Phoenix.Presence` so `presence_state` + `presence_diff` keep
 *     the local view fresh; pushes aggregated `PresenceInfo` into the
 *     Zustand store on every onSync.
 *   - Tracks `presence:offline_confirmed` events from the grace GenServer
 *     so the UI can swap "disconnected" for true "offline + last_seen".
 *   - Tears all channels down on unmount.
 *
 * No-op until auth is hydrated.
 */

import { useEffect, useRef, useState } from 'react'
import { Channel, Presence } from 'phoenix'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { getPhoenixSocket } from '@/lib/phoenix-socket'
import {
  type PresenceInfo,
  type PresenceStatus,
  usePresenceStore,
} from '../store/presence.store'

export interface RawMeta {
  status?:         string
  manual_status?:  string | null
  custom_message?: string | null
  /** ISO 8601 — when `custom_message` clears itself; null/absent = never. */
  custom_message_expires_at?: string | null
  in_meeting?:     boolean
  device?:         string
  user_name?:      string
  /** This user turned "Show status" off. `can-watch` already blocks NEW
   *  watchers, but an already-joined one keeps its subscription — so the meta
   *  carries the flag and we resolve them to offline right away. */
  hidden?:         boolean
}

const STATUS_RANK: Record<string, number> = { dnd: 4, busy: 3, away: 2, online: 1 }

const isManual = (v: unknown): v is PresenceStatus =>
  v === 'dnd' || v === 'busy' || v === 'away' || v === 'online'

/**
 * The meta whose manual slice represents the user — most restrictive wins.
 *
 * Returns the META, not just the status, so the status and the custom message
 * shown together come from ONE device. They used to be picked independently
 * (most-restrictive status, first-non-empty message), which showed a user with
 * "dnd" on one tab and "brb lunch" on another as *dnd + "brb lunch"* — a pair
 * that never existed on any device. Mirrors `Signaling.PresenceAggregator`.
 */
function pickManualMeta(metas: RawMeta[]): RawMeta | null {
  let best: RawMeta | null = null
  for (const m of metas) {
    if (!isManual(m.manual_status)) continue
    if (!best || (STATUS_RANK[m.manual_status] ?? 0) > (STATUS_RANK[best.manual_status as string] ?? 0)) {
      best = m
    }
  }
  return best
}

export function aggregate(userId: string, metas: RawMeta[]): PresenceInfo {
  // "Show status" off — indistinguishable from offline, and no last-seen. Checked
  // before anything else so a hidden user can't leak status through a second
  // device's meta.
  if (metas.length === 0 || metas.some((m) => m.hidden === true)) {
    return {
      userId, status: 'offline',
      manualStatus: null, customMessage: null, inMeeting: false, lastSeenAt: null,
      hidden: metas.some((m) => m.hidden === true),
    }
  }
  // One winning meta, then read the whole manual slice off it — see
  // `pickManualMeta`. `in_meeting` stays an any-tab check: it describes the
  // person, not a device.
  const winner = pickManualMeta(metas)
  const manualStatus = (winner?.manual_status as PresenceStatus | undefined) ?? null
  const inMeeting = metas.some((m) => m.in_meeting === true)
  // With a winner, the message comes from THAT meta and nowhere else — even
  // when it has none. Falling back to another tab is what produced the
  // mismatch: a "dnd" tab with no message beside an "online" tab with "brb
  // lunch" rendered as dnd + "brb lunch", a pair that existed on no device.
  //
  // With NO winner there is no status for a message to disagree with — a custom
  // message does not require a manual status — so any tab's message is
  // reportable. Mirrors `Signaling.PresenceAggregator`.
  //
  // The expiry is read off the same meta as the message, so the two always
  // describe one message.
  const now = Date.now()
  const messageMeta = winner
    ? (hasLiveMessage(winner, now) ? winner : null)
    : metas.find((m) => hasLiveMessage(m, now)) ?? null
  const customMessage = messageMeta?.custom_message ?? null
  const customMessageExpiresAt = messageMeta?.custom_message_expires_at ?? null

  const status: PresenceStatus = manualStatus ?? (inMeeting ? 'busy' : 'online')

  return {
    userId, status, manualStatus, customMessage, customMessageExpiresAt, inMeeting,
    lastSeenAt: null, hidden: false,
  }
}

/**
 * A non-empty message that has not reached its expiry yet.
 *
 * The server clears an expired message from the meta itself and the diff
 * follows; this only covers a diff for some other change landing in between.
 */
function hasLiveMessage(m: RawMeta, now: number): m is RawMeta & { custom_message: string } {
  if (typeof m.custom_message !== 'string' || m.custom_message === '') return false
  const expiresAt = m.custom_message_expires_at ? Date.parse(m.custom_message_expires_at) : NaN
  return Number.isNaN(expiresAt) || expiresAt > now
}

interface ChannelHandle {
  channel: Channel
  presence: Presence
}

/**
 * Join errors that retrying cannot fix. `invalid_topic` means the id itself is
 * malformed — a fresh attempt produces the same answer.
 *
 * NOTE `forbidden` is deliberately NOT here. The server authorizes a watcher
 * against the WATCHER's own `contactPresenceId` (`user_channel.ex` → `can_watch?`),
 * so a socket that connects before that identity resolves is told "forbidden"
 * for a contact it is genuinely allowed to watch. Treating that as permanent is
 * what left contacts stuck showing offline for a whole session.
 */
const TERMINAL_JOIN_REASONS = new Set(['invalid_topic'])
/** Retry budget per topic — 1s, 2s, 4s, 8s. */
const JOIN_MAX_ATTEMPTS = 4
const JOIN_RETRY_BASE_MS = 1000
/**
 * After the retry budget runs out, try again this much later. A watch refused
 * while the peer hid their status stays refused only until they show it again,
 * and nothing tells this client when that happens, so without a late retry the
 * peer showed Offline until the next reconnect.
 */
const DENIED_RETRY_MS = 60_000
/** Late retries back off (1, 2, 4... minutes) so a contact who is never watchable costs little. */
const DENIED_RETRY_MAX_MS = 10 * 60_000

export function usePresenceSubscription(userIds: string[]): void {
  const me = useAuthStore((s) => s.user)
  const channelsRef = useRef<Map<string, ChannelHandle>>(new Map())
  // Topics we've stopped attempting: a terminal reason, or a transient one that
  // exhausted its retries. phoenix-js auto-rejoins errored channels on a
  // 1s/2s/5s/10s backoff (channel.js:47), which floods Phoenix with
  // `presence.acl_denied` — so on error we `channel.leave()` to cancel that timer
  // and track the topic here, running our own bounded backoff instead.
  const deniedRef = useRef<Set<string>>(new Set())
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // Late retries: how many a topic has had, and which are waiting on one.
  const lateCountRef = useRef<Map<string, number>>(new Map())
  const latePendingRef = useRef<Set<string>>(new Set())
  // Bumped to re-run the join effect after a reconnect clears the blacklist.
  const [attempt, setAttempt] = useState(0)

  // Normalize input to a stable, deduped, sorted comma string so the effect
  // doesn't re-fire on equal-but-new-array references.
  const key = Array.from(new Set(userIds)).filter(Boolean).sort().join(',')

  // Reset the denied set when the viewer changes (login switch / logout).
  useEffect(() => { deniedRef.current.clear() }, [me?.id])

  useEffect(() => {
    if (!me?.id) return
    let cancelled = false

    const targetIds = key ? key.split(',') : []
    const current = channelsRef.current
    const denied = deniedRef.current

    // Leave channels no longer requested.
    for (const [id, handle] of current.entries()) {
      if (!targetIds.includes(id)) {
        try { handle.channel.leave() } catch {/* ignore */}
        current.delete(id)
      }
    }

    // Join channels newly requested.
    ;(async () => {
      const socket = await getPhoenixSocket(me.id, me.name)

      // A reconnect re-authenticates and may resolve an identity that wasn't
      // available on the first attempt — so give every previously-failed topic a
      // clean slate rather than carrying the blacklist across sockets.
      socket.onOpen(() => {
        if (denied.size === 0) return
        denied.clear()
        setAttempt((n) => n + 1)
      })

      const joinTarget = (targetId: string, attempt: number): void => {
        if (cancelled || current.has(targetId) || denied.has(targetId)) return

        const channel = socket.channel(`user:${targetId}`, {})
        const presence = new Presence(channel)

        presence.onSync(() => {
          const list = presence.list<{ key: string; metas: RawMeta[] }>(
            (key, p) => ({ key, metas: p.metas as RawMeta[] }),
          )
          const found = list.find((row) => row.key === targetId)
          const info = aggregate(targetId, found?.metas ?? [])
          usePresenceStore.getState().setOne(info)
        })

        channel.on('presence:offline_confirmed', (payload: { user_id: string; last_seen_at: string }) => {
          usePresenceStore.getState().markOffline(payload.user_id, payload.last_seen_at)
        })

        // The server has withdrawn permission to watch this user (a block, in
        // either direction) and closes the topic right behind this frame.
        //
        // The store holds the last status we were sent, and once the
        // subscription is gone nothing will ever arrive to correct it — so
        // without this the blocked side keeps rendering "online" indefinitely.
        // `blockedIds` masking does not cover it: only the BLOCKER knows to set
        // that list, and this is the view of the person who was blocked.
        //
        // Not added to `denied`: the block can be lifted, and `denied` is only
        // for join attempts the server has actually refused. Dropping the
        // channel from `current` lets a later effect run re-join and let the
        // server re-run the ACL.
        channel.on('presence:watch_revoked', (payload: { user_id?: string }) => {
          const revoked = payload?.user_id ?? targetId
          try { channel.leave() } catch {/* ignore */}
          current.delete(revoked)
          usePresenceStore.getState().setOne(aggregate(revoked, []))
        })

        const onJoinFailure = (reason: string) => {
          // Cancel phoenix-js's own rejoin timer (1/2/5/10s) — we run a bounded
          // backoff here instead so a permanent denial can't loop forever.
          try { channel.leave() } catch {/* ignore */}
          current.delete(targetId)
          // Render *something* while we retry. A `forbidden` join is what a
          // contact hiding their status looks like from here (see the note
          // on TERMINAL_JOIN_REASONS below): render that row as hidden
          // rather than a bare offline placeholder that could show a stale
          // last-seen. Any other failure reason is a genuine unknown, so it
          // keeps the ordinary placeholder.
          usePresenceStore.getState().setOne(
            reason === 'forbidden'
              ? { ...aggregate(targetId, []), hidden: true }
              : aggregate(targetId, []),
          )

          const terminal = TERMINAL_JOIN_REASONS.has(reason)
          if (terminal || attempt + 1 >= JOIN_MAX_ATTEMPTS) {
            denied.add(targetId)
            if (!terminal) {
              const n = lateCountRef.current.get(targetId) ?? 0
              lateCountRef.current.set(targetId, n + 1)
              latePendingRef.current.add(targetId)
              const late = setTimeout(() => {
                timersRef.current.delete(targetId)
                latePendingRef.current.delete(targetId)
                denied.delete(targetId)
                joinTarget(targetId, 0)
              }, Math.min(DENIED_RETRY_MS * 2 ** n, DENIED_RETRY_MAX_MS))
              timersRef.current.set(targetId, late)
            }
            return
          }
          const timer = setTimeout(
            () => { timersRef.current.delete(targetId); joinTarget(targetId, attempt + 1) },
            JOIN_RETRY_BASE_MS * 2 ** attempt,
          )
          timersRef.current.set(targetId, timer)
        }

        channel
          .join()
          // Only a join the server ACCEPTED ends the run of late retries.
          // Resetting the count straight after join() — before any answer —
          // zeroed it on every attempt, so the back-off below never grew:
          // a contact who can never be watched was retried every ~67 s for
          // the whole session instead of 1, 2, 4 … 10 minutes apart.
          .receive('ok', () => { lateCountRef.current.delete(targetId) })
          .receive('error', (payload: { reason?: string } | undefined) =>
            onJoinFailure(payload?.reason ?? 'unknown'))
          .receive('timeout', () => onJoinFailure('timeout'))

        if (cancelled) {
          try { channel.leave() } catch {/* ignore */}
          return
        }
        current.set(targetId, { channel, presence })
      }

      for (const targetId of targetIds) {
        if (cancelled) break
        joinTarget(targetId, 0)
      }
    })().catch(() => { /* socket teardown / token miss — bail silently */ })

    return () => {
      cancelled = true
      for (const t of timersRef.current.values()) clearTimeout(t)
      timersRef.current.clear()
      // A cancelled late retry must not leave its topic denied for good: the
      // next run gets to try it again.
      for (const id of latePendingRef.current) denied.delete(id)
      latePendingRef.current.clear()
    }
  }, [key, me?.id, me?.name, attempt])

  // On full unmount of the consumer, drop every channel we own.
  useEffect(() => {
    const owned = channelsRef.current
    return () => {
      for (const handle of owned.values()) {
        try { handle.channel.leave() } catch {/* ignore */}
      }
      owned.clear()
    }
  }, [])
}
